import { useState } from 'react';
import styled from 'styled-components';
import { IoEyeOffOutline, IoEyeOutline } from 'react-icons/io5';
import { Footer, Header, Wrapper } from '../../../general_components/forms/Form';
import { DragableList, DragHandle } from '../../../general_components/content_management/item_page/dragableList';
import { API_ROOT, sendRequest } from '../../../supplementary_components/requests';
import { addNotification } from '../../../supplementary_components/store';
import { retrieveCustomization, storeCustomizations } from '../../../supplementary_components/userDataDelegate';
import {
    TRUCK_BLOCK_LABELS,
    editorTruckBlocks,
    mergePageLayout,
    readSavedTruckPageLayout,
    truckLayoutSaveValue,
} from '../../trucks/truckPageLayout';
import styles from './truckPageLayoutPopup.module.scss';

const PAGE_KEY = 'truck';
const DRAG_HANDLE = 'layoutDragHandle';

const LayoutFooter = styled(Footer)`
    justify-content: space-between;
    && > div:nth-child(2) {
        display: flex;
        align-items: center;
        background: transparent;
        box-shadow: none;
        color: inherit;
        padding: 0;
        margin: 0;
    }
`;

const currentRole = () => ({
    roleCode: localStorage.getItem('roleCode') || '',
    roleName: localStorage.getItem('roleName') || '',
});

export const TruckPageLayoutPopup = ({ closePopup }) => {
    const role = currentRole();
    const [items, setItems] = useState(() => editorTruckBlocks({
        ...role,
        saved: readSavedTruckPageLayout(),
    }));
    const [saving, setSaving] = useState(false);

    const reset = () => {
        setItems(editorTruckBlocks({ ...role, saved: null }));
    };

    const toggleShown = (id) => {
        setItems((current) => current.map((item) => (
            item.id === id ? { ...item, shown: !item.shown } : item
        )));
    };

    const finishDrag = (oldIndex, newIndex) => {
        if (oldIndex !== newIndex) {
            setItems((current) => {
                const next = current.slice();
                const [item] = next.splice(oldIndex, 1);
                next.splice(newIndex, 0, item);
                return next;
            });
        }
        return true;
    };

    const save = () => {
        if (saving) return;
        setSaving(true);
        const pageValue = truckLayoutSaveValue({ items, ...role });
        const pageLayouts = mergePageLayout(retrieveCustomization('pageLayouts'), PAGE_KEY, pageValue);
        sendRequest(
            'PATCH',
            API_ROOT + 'employees/customizations',
            null,
            (updated) => {
                storeCustomizations(updated);
                setSaving(false);
                addNotification({ message: 'Truck page layout saved', type: 'success' });
                closePopup();
            },
            (error) => {
                setSaving(false);
                addNotification({
                    message: error?.customError || 'Failed to save truck page layout',
                    type: 'alert',
                });
            },
            { pageLayouts },
        );
    };

    const BlockList = () => (
        <>
            {items.map((item) => (
                <div key={item.id} className={`${styles.row} ${item.shown ? '' : styles.hidden}`}>
                    <div className={styles.handle}>
                        <DragHandle handleClassName={DRAG_HANDLE} />
                    </div>
                    <span className={styles.label}>{TRUCK_BLOCK_LABELS[item.id] || item.id}</span>
                    <button type="button" className={styles.visibility} onClick={() => toggleShown(item.id)}>
                        {item.shown ? <IoEyeOutline /> : <IoEyeOffOutline />}
                        <span>{item.shown ? 'Hide' : 'Show'}</span>
                    </button>
                </div>
            ))}
        </>
    );

    return (
        <Wrapper onClick={(event) => event.stopPropagation()}>
            <Header><span>Truck Page</span></Header>
            <div className={styles.body}>
                <p className={styles.hint}>
                    Drag to reorder the center column. Blocks you cannot access are not listed.
                </p>
                <DragableList component={BlockList} handleClassName={DRAG_HANDLE} finishDrag={finishDrag} />
            </div>
            <LayoutFooter>
                <div className="non-selectable" onClick={reset}>Reset</div>
                <div className={styles.actions}>
                    <div className="non-selectable" onClick={closePopup}>Cancel</div>
                    <div className={saving ? 'disabled' : ''}>
                        <input type="button" value={saving ? 'Saving…' : 'Save'} disabled={saving} onClick={save} />
                    </div>
                </div>
            </LayoutFooter>
        </Wrapper>
    );
};
